import { useMemo, useState } from 'react';
import { VIOLATION_STATUSES } from '@shared/types';
import type { Attachment, Violation, ViolationStatus, ViolationTag } from '@shared/types';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { relativeTime } from '../lib/format';
import { AttachmentGrid, AttachmentUploader } from '../components/Attachments';
import { NoteThread } from '../components/NoteThread';
import { EmptyState, Field, LoadingPage, Tag, ViolationStatusTag } from '../components/ui';
import { PageHeader } from '../components/PageHeader';

export function ViolationsPage() {
  const { user, isBoard } = useAuth();
  const { notify, fail } = useToast();
  const [filter, setFilter] = useState<'Needs action' | 'All' | ViolationStatus>(
    isBoard ? 'Needs action' : 'All',
  );
  const [showForm, setShowForm] = useState(!isBoard);

  const state = useAsync(async () => (await api.violations()).violations, [user?.id]);
  const tags = useAsync(async () => (await api.violationTags()).tags, []);

  const items = state.data ?? [];
  const visible = useMemo(() => {
    if (filter === 'All') return items;
    if (filter === 'Needs action') {
      return items.filter((item) => item.status === 'Open' || item.status === 'Acknowledged' || item.status === 'Notice sent');
    }
    return items.filter((item) => item.status === filter);
  }, [items, filter]);

  function replace(updated: Violation) {
    state.setData(items.map((item) => (item.id === updated.id ? updated : item)));
  }

  if (state.loading && !state.data) return <LoadingPage label="Loading violations…" />;

  return (
    <div>
      <PageHeader
        title="Violations"
        lede={
          isBoard
            ? 'Reports from the neighborhood: landscaping, trash bins, noise, parking, and any flags you add.'
            : 'See something that is out of compliance? Open this on your phone, enter the address, and take a photo.'
        }
      />

      <div className="row" style={{ marginBottom: 16 }}>
        <button type="button" className="btn" onClick={() => setShowForm((open) => !open)}>
          {showForm ? 'Close form' : 'Report a violation'}
        </button>
      </div>

      {showForm && (
        <ViolationForm
          tags={tags.data ?? []}
          onCreated={(violation) => {
            state.setData([violation, ...items]);
            setShowForm(false);
            notify('Violation sent to the board.', 'success');
          }}
          onError={fail}
          onTagsChange={(next) => tags.setData(next)}
        />
      )}

      <div className="filters" role="tablist" aria-label="Filter violations">
        {(['Needs action', 'All', ...VIOLATION_STATUSES] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={filter === option}
            className={filter === option ? 'on' : ''}
            onClick={() => setFilter(option)}
          >
            {option}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title={filter === 'All' ? 'No violations yet.' : 'Nothing in this filter.'}
          hint="Standing at the property? Use Report a violation and the camera button."
        />
      ) : (
        visible.map((item) => (
          <ViolationCard key={item.id} violation={item} onChange={replace} />
        ))
      )}
    </div>
  );
}

function ViolationForm({
  tags,
  onCreated,
  onError,
  onTagsChange,
}: {
  tags: ViolationTag[];
  onCreated: (violation: Violation) => void;
  onError: (error: unknown, fallback: string) => void;
  onTagsChange: (tags: ViolationTag[]) => void;
}) {
  const { isBoard } = useAuth();
  const [propertyAddress, setPropertyAddress] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [newTag, setNewTag] = useState('');

  function toggle(id: string) {
    setSelected((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  }

  async function addTag() {
    const name = newTag.trim();
    if (!name) return;
    try {
      const { tags: next, tag } = await api.createViolationTag(name);
      onTagsChange(next);
      setSelected((current) => [...current, tag.id]);
      setNewTag('');
    } catch (error) {
      onError(error, 'That tag was not created.');
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { violation } = await api.violationCreate({
        propertyAddress,
        description,
        tagIds: selected,
        attachmentIds: files.map((file) => file.id),
      });
      onCreated(violation);
    } catch (error) {
      onError(error, 'The violation was not submitted.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h3>Report a violation</h3>
      <Field label="Property address">
        <input
          value={propertyAddress}
          onChange={(event) => setPropertyAddress(event.target.value)}
          required
          autoComplete="street-address"
          placeholder="18 Willow Drive"
        />
      </Field>
      <span className="label-text">Flags</span>
      <div className="chip-row">
        {tags.map((tag) => (
          <button
            key={tag.id}
            type="button"
            className={selected.includes(tag.id) ? 'chip on' : 'chip'}
            onClick={() => toggle(tag.id)}
          >
            {tag.name}
          </button>
        ))}
      </div>
      {isBoard && (
        <div className="row" style={{ marginTop: 8 }}>
          <input
            value={newTag}
            onChange={(event) => setNewTag(event.target.value)}
            placeholder="Add a flag, e.g. Boat stored"
          />
          <button type="button" className="btn s" onClick={() => void addTag()}>
            Add flag
          </button>
        </div>
      )}
      <Field label="What did you see?" hint="Optional, but it helps the board follow up.">
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={3}
          placeholder="Bins left at the curb since Monday. Two photos attached."
        />
      </Field>
      <span className="label-text">Photos</span>
      <AttachmentUploader
        value={files}
        onChange={setFiles}
        accept="image/*"
        label="Choose from library"
        camera
      />
      <div className="btn-row">
        <button type="submit" className="btn" disabled={busy || selected.length === 0}>
          {busy ? 'Sending…' : 'Submit violation'}
        </button>
      </div>
    </form>
  );
}

function ViolationCard({
  violation,
  onChange,
}: {
  violation: Violation;
  onChange: (violation: Violation) => void;
}) {
  const { isBoard } = useAuth();
  const { fail } = useToast();
  const [files, setFiles] = useState<Attachment[]>([]);

  async function setStatus(status: ViolationStatus) {
    try {
      const { violation: updated } = await api.violationUpdate(violation.id, status);
      onChange(updated);
    } catch (error) {
      fail(error, 'Status was not updated.');
    }
  }

  async function addFiles() {
    if (files.length === 0) return;
    try {
      const { violation: updated } = await api.violationAddFiles(
        violation.id,
        files.map((file) => file.id),
      );
      setFiles([]);
      onChange(updated);
    } catch (error) {
      fail(error, 'Photos were not attached.');
    }
  }

  return (
    <article className="card">
      <div className="card-head">
        <div>
          <h3>{violation.propertyAddress}</h3>
          <div className="mut">
            {violation.reference} · {violation.authorName}
            {violation.authorUnit ? ` · ${violation.authorUnit}` : ''} · {relativeTime(violation.createdAt)}
          </div>
        </div>
        <ViolationStatusTag status={violation.status} />
      </div>
      <div className="tag-row" style={{ margin: '8px 0' }}>
        {violation.tags.map((tag) => (
          <Tag key={tag.id} tone="warn">
            {tag.name}
          </Tag>
        ))}
      </div>
      {violation.description && <p>{violation.description}</p>}
      <AttachmentGrid items={violation.attachments} />

      {isBoard && (
        <div className="row" style={{ marginTop: 10 }}>
          {VIOLATION_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              className={violation.status === status ? 'btn tiny' : 'btn s tiny'}
              onClick={() => void setStatus(status)}
            >
              {status}
            </button>
          ))}
        </div>
      )}

      <AttachmentUploader value={files} onChange={setFiles} accept="image/*" label="Add more photos" />
      {files.length > 0 && (
        <div className="btn-row">
          <button type="button" className="btn s" onClick={() => void addFiles()}>
            Attach photos
          </button>
        </div>
      )}

      <NoteThread
        notes={violation.notes}
        placeholder="Add a note for the board or the reporter"
        allowInternal={isBoard}
        onSubmit={async (body, internal, attachmentIds) => {
          const { violation: updated } = await api.violationAddNote(
            violation.id,
            body,
            internal,
            attachmentIds,
          );
          onChange(updated);
        }}
      />
    </article>
  );
}
